import React, {useEffect, useState} from 'react';
import Resources from "../../../../../data/services/resources";
import {Field, FieldsManager} from "../../../../../data/services/fields";
import {getDefaultTableOptions, updateQueryFields} from "../../../../util/util-helpers";
import ResourceTableDialogNew from "../../resource-table-dialog-new";
import FieldsForm from "../../../fields/fields-form";
import InfoBar from "../../../info-paragraph/info-bar";
import {useDispatch, useSelector} from "react-redux";
import ResourceTable from "../../../resource-table";
import ModalDefault from "../../modal-default";
import {updateResource} from "../../../../../data/actions/resource";
import LocalStorage from "../../../../util/localStorage";
import {updateResourceDiff} from "../../../../../data/actions/resourceDiff";

export default function TaskReassignDialog({translate, show, onClose, piggyQuery, getTableFields}) {
    const dispatch = useDispatch();
    const tableKey = 'TaskID';

    const [ExcludeAssigneeID, setExcludeAssigneeID] = useState("");
    const [fields, setFields] = useState(() => getFields());
    const [selectedRows, setSelectedRows] = useState({});
    const [previewRows, setPreviewRows] = useState({});
    const [isPreviewSelectedDialogOpen, setIsPreviewSelectedDialogOpen] = useState(false);
    const selectedRowsCount = Object.keys(selectedRows)?.length;
    const hasSelectedRows = !!selectedRowsCount;

    const resource = useSelector(state => state.dialogResource);

    function handleInputChange(name, value) {
        setFields(Object.assign({}, FieldsManager.updateField(fields, name, value)))
    }

    function handleUpdateQueryFields(queryFields, name, value) {
        if (name === 'ExcludeAssigneeID') {
            setExcludeAssigneeID(value);
        }

        return updateQueryFields(queryFields, name, value);
    }

    function handleSelectRowClick(item) {
        const itemID = item?.TaskID;

        let selectedRowsUpdate = Object.assign({}, selectedRows);

        if (selectedRowsUpdate[itemID]) {
            delete selectedRowsUpdate[itemID]
        } else {
            Object.assign(selectedRowsUpdate, {[itemID]: item})
        }

        setSelectedRows(selectedRowsUpdate);
    }

    function handleSelectAllClick(selectAll) {
        const data = Object.values(resource?.data?.list);

        let selected = Object.assign({}, selectedRows)

        if (!selectAll) {
            selected = Object.assign(selected, data.reduce((memo, it) => {
                memo[it[tableKey]] = it
                return memo
            }, {}))
        } else {
            let selectedRowsKeys = Object.keys(selected)
            data.forEach(it => {
                if (selectedRowsKeys.includes(it[tableKey].toString())) {
                    delete selected[it[tableKey]]
                }
            })
        }

        setSelectedRows(selected);
    }

    function handleSelectPreviewRowClick(item) {
        const itemID = item?.TaskID;

        let previewRowsUpdate = Object.assign({}, previewRows);

        if (previewRowsUpdate[itemID]) {
            delete previewRowsUpdate[itemID]
        } else {
            Object.assign(previewRowsUpdate, {[itemID]: item})
        }

        setPreviewRows(previewRowsUpdate);
    }

    function handlePreviewSelectAllClick(selectAll) {
        const data = Object.values(selectedRows);

        let selected = Object.assign({}, previewRows);

        if (!selectAll) {
            selected = Object.assign(selected, data.reduce((memo, it) => {
                memo[it[tableKey]] = it
                return memo
            }, {}))
        } else {
            let selectedRowsKeys = Object.keys(selected)
            data.forEach(it => {
                if (selectedRowsKeys.includes(it[tableKey].toString())) {
                    delete selected[it[tableKey]]
                }
            })
        }

        setPreviewRows(selected);
    }

    function getFields() {
        return {
            ContactID: new Field('ContactID', "", [''], false, 'select-search', {
                label: "Assignee",
                htmlBefore: () => {
                    return <InfoBar addClass="col-span-full mb-4">Select an assignee and one or more tasks from the
                        table. The selected tasks will be assigned to the chosen assignee.</InfoBar>
                }
            })
        }
    }

    function getQueryFields(ExcludeAssigneeID) {
        return {
            query: new Field('query', '', [''], false, 'search', {
                addContainerClass: "col-span-2",
                hideLabel: true,
                labelType: "float",
                autofocus: true,
            }),
            offset: new Field('offset', "0", [''], false, 'hidden'),
            limit: new Field("limit", "50", [''], false, 'select', {
                hideLabel: true,
                labelType: "float"
            }, {menuPlacement: "top"}),
            sort: new Field('sort', "DESC", [''], false, 'hidden'),
            sortBy: new Field('sortBy', tableKey, [''], false, 'hidden'),
            ExcludeAssigneeID: new Field('ExcludeAssigneeID', ExcludeAssigneeID, [''], false, 'hidden'),
            TaskStatus: new Field('TaskStatus', "1,2", [''], false, 'hidden'),
        }
    }

    useEffect(() => {
        setSelectedRows({});
        setFields(getFields());
    }, [show]);

    return <ResourceTableDialogNew
        show={show}
        onSubmitButton={() => {
            dispatch(updateResourceDiff({
                user: LocalStorage.get('user'),
                params: {
                    Tasks: Object.values(selectedRows).map(it => it.TaskID),
                    AssigneeID: fields.ContactID?.value?.value
                },
                resource: Resources.TasksBulkReassign,
                errorMessage: true,
                successMessage: "Tasks successfully assigned!",
                piggyResource: Resources.Tasks,
                query: piggyQuery
            }));
            onClose();
        }}
        hideRowsPerPage={false}
        buttonLabel={"Assign selected tasks to assignee"}
        secondaryButtonDisabled={!selectedRowsCount}
        secondaryButtonLabel={
            <span
                className={hasSelectedRows ? 'pr-8' : undefined}
            >
                {
                    translate('btn.view_selected_tasks')
                }

                {hasSelectedRows && (
                    <div
                        className="absolute right-0 top-0 bottom-0 w-10 bg-inverse border-l border-primary rounded-r-btn flex items-center justify-center"
                    >
                        <span className="leading-5 text-xs">
                            {selectedRowsCount}
                        </span>
                    </div>
                )}
            </span>
        }
        onSecondaryButtonClick={() => {
            setIsPreviewSelectedDialogOpen(true);
            setPreviewRows(Object.assign({}, selectedRows));
        }}
        onSelectRow={handleSelectRowClick}
        onSelectAllClick={handleSelectAllClick}
        buttonDisabled={!hasSelectedRows || !fields.ContactID?.value?.value}
        selectedRows={selectedRows}
        htmlBefore={
            <div className="py-4 border-b border-tm-gray-200 relative z-50">
                <div className="max-w-md mx-auto">
                    <FieldsForm
                        fieldsState={fields}
                        onChange={handleInputChange}
                        selects={{
                            ContactID: {
                                api: "api/" + Resources.Contacts,
                                query: {
                                    limit: 10,
                                    offset: 0,
                                    IsSystemUser: 1
                                },
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName,
                                    Contact: item
                                })
                            }
                        }}
                        translate={translate}
                    />
                </div>
            </div>
        }
        resourcePath={Resources.Tasks}
        tableKey={tableKey}
        title={translate("text.bulk_reassign_tasks")}
        onUpdateQueryFields={handleUpdateQueryFields}
        options={getDefaultTableOptions(getTableFields(), {}, 'task-reassign-dialog', translate)}
        widthClass={"max-w-full"}
        maxHeightClass="max-h-[calc(100vh-29rem)]"
        translate={translate}
        onClose={onClose}
        fields={getTableFields()}
        getQueryFields={getQueryFields(ExcludeAssigneeID)}
        htmlAfter={
            <ModalDefault
                show={isPreviewSelectedDialogOpen}
                widthClass={'max-w-7xl'}
                translate={translate}
                title={translate('modal_heading.review_selected_trucks')}
                buttonLabel={translate('btn.Update')}
                onButtonClick={() => {
                    setSelectedRows(Object.assign({}, previewRows));
                    setIsPreviewSelectedDialogOpen(false);
                }}
                buttonDisabled={false}
                onClose={() => setIsPreviewSelectedDialogOpen(false)}
                closeButtonLabel={translate('btn.cancel')}
            >
                <ResourceTable
                    data={Object.values(selectedRows)}
                    count={selectedRowsCount}

                    tableKey={"TaskID"}
                    onSelectRow={handleSelectPreviewRowClick}
                    selectedRows={previewRows}
                    onSelectAllClick={handlePreviewSelectAllClick}

                    fields={getTableFields()}

                    translate={translate}

                    options={getDefaultTableOptions(getTableFields(), {}, 'task-reassign-dialog', translate)}
                />
            </ModalDefault>
        }
    />
}

